import Reveal from "@/components/Reveal";
import DonateButton from "@/components/DonateButton";
import { IMG } from "@/lib/site";

const GORILLA = "https://images.unsplash.com/photo-1773975006646-04184f1eb137?auto=format&fit=crop&w=1400&q=80";

const MINERALS = [
  { name: "Cobalt", desc: "Essential for lithium-ion batteries in phones, laptops, and EVs", badge: "80–90% of the world's supply" },
  { name: "Coltan", desc: "Used in tantalum capacitors in all electronics" },
  { name: "Copper", desc: "Vital for electrical wiring and green technology" },
  { name: "Gold · Tin · Tungsten · Zinc · Platinum · Palladium · Silver", desc: "Mined extensively, powering global industry" },
  { name: "Lithium", desc: "Emerging reserves still being explored" },
];

const CROPS = ["Rubber", "Palm oil", "Hardwood timber", "Coffee", "Cocoa", "Tea", "Sugar", "Cotton"];

const INDUSTRIAL = [
  { name: "Diamonds", desc: "Among the world's notable producers" },
  { name: "Iron Ore", desc: "Raw material for global steel" },
  { name: "Manganese", desc: "Essential to steel and batteries" },
  { name: "Uranium", desc: "Mined since the 1940s" },
  { name: "Inga Dam", desc: "One of the world's largest potential energy projects" },
  { name: "The Congo River", desc: "The second-largest river on Earth by volume" },
];

function BlockNumber({ n }) {
  return <p className="font-mono text-xs tracking-[0.35em] text-gold">{n}</p>;
}

export default function WhyCongoSection() {
  return (
    <section id="why-congo" className="relative scroll-mt-20 overflow-hidden bg-forest py-24 text-ivory lg:py-32" data-testid="why-congo-section">
      <div className="grain absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        {/* HEADLINE */}
        <Reveal>
          <p className="eyebrow text-gold" data-testid="why-congo-eyebrow">Why Congo?</p>
          <h2
            className="mt-6 max-w-5xl text-balance font-display text-[clamp(2.6rem,6.5vw,5.75rem)] font-semibold leading-[1.02] tracking-tight"
            data-testid="why-congo-headline"
          >
            Where would the world be without <em className="italic text-gold">Congo?</em>
          </h2>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ivory/75">
            A country with extraordinary natural, cultural and human wealth — this is the soul of our mission.
          </p>
        </Reveal>

        {/* 01 — LUNGS OF THE EARTH */}
        <div className="mt-24 grid items-center gap-12 lg:grid-cols-12" data-testid="why-congo-lungs">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl">
                <img src={IMG.forestBank} alt="Dense Congo Basin rainforest along a river" className="aspect-[4/5] w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-ivory/15" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <BlockNumber n="01" />
              <h3 className="mt-4 font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">The Lungs of the Earth</h3>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-ivory/80">
                Congo's rainforest is the second-largest in the world, absorbing more carbon than the Amazon. Scientists call it one of the most powerful breathing systems on Earth.
              </p>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-ivory/80">
                Its trees filter our air, stabilize the global climate, and support ecosystems that keep the planet alive.
              </p>
            </Reveal>
          </div>
        </div>

        {/* 02 — $24 TRILLION */}
        <div className="mt-24 text-center" data-testid="why-congo-24t">
          <Reveal>
            <BlockNumber n="02" />
            <p className="mt-6 font-display text-[clamp(3.5rem,11vw,9.5rem)] font-semibold leading-none tracking-tight text-gold" data-testid="why-congo-24t-figure">
              $24 <em className="italic">Trillion</em>
            </p>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.3em] text-ivory/80">In wealth — none of it returned</p>
            <p className="mx-auto mt-7 max-w-3xl text-lg leading-relaxed text-ivory/80">
              Congo holds an estimated $24 trillion in natural resources, making it the wealthiest country in the world by natural assets. Yet the communities who power global progress see almost none of that wealth.
            </p>
          </Reveal>
        </div>

        {/* 03 — MODERN TECHNOLOGY */}
        <div className="mt-24" data-testid="why-congo-minerals">
          <Reveal>
            <BlockNumber n="03" />
            <h3 className="mt-4 max-w-3xl font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">Modern technology runs on Congo.</h3>
          </Reveal>
          <div className="mt-12">
            {MINERALS.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.05} y={14}>
                <div className="flex flex-col gap-2 border-b border-ivory/10 py-6 sm:flex-row sm:items-center sm:justify-between sm:gap-8" data-testid={`why-congo-mineral-${i + 1}`}>
                  <p className="font-display text-xl font-medium sm:text-2xl">
                    {m.name}
                    {m.badge && (
                      <span className="ml-4 inline-block translate-y-[-2px] rounded-full bg-gold px-3 py-1 align-middle font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-forest" data-testid="why-congo-cobalt-badge">
                        {m.badge}
                      </span>
                    )}
                  </p>
                  <p className="max-w-md text-sm leading-relaxed text-ivory/65 sm:text-right">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* 04 + 05 — AGRICULTURE & CULTURE */}
        <div className="mt-24 grid gap-6 md:grid-cols-2" data-testid="why-congo-agriculture">
          <Reveal>
            <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-ivory/10 bg-ivory/5">
              <img src={IMG.womanFarming} alt="A woman working fertile farmland" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <div className="flex flex-1 flex-col p-8">
                <BlockNumber n="04" />
                <h3 className="mt-3 font-display text-2xl font-semibold sm:text-3xl">Agricultural &amp; Biological Wealth</h3>
                <p className="mt-4 flex-1 leading-relaxed text-ivory/75">
                  Congo's fertile land feeds and supplies global industries.
                </p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {CROPS.map((c) => (
                    <li key={c} className="rounded-full border border-ivory/25 px-3.5 py-1.5 text-xs uppercase tracking-wider text-ivory/85">{c}</li>
                  ))}
                </ul>
              </div>
            </article>
          </Reveal>
          <Reveal delay={0.1}>
            <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-ivory/10 bg-ivory/5" data-testid="why-congo-culture">
              <img src={GORILLA} alt="A gorilla mother carrying her baby through lush green forest" className="aspect-[4/3] w-full object-cover" loading="lazy" />
              <div className="flex flex-1 flex-col p-8">
                <BlockNumber n="05" />
                <h3 className="mt-3 font-display text-2xl font-semibold sm:text-3xl">Cultural &amp; Ecological Wealth</h3>
                <p className="mt-4 flex-1 leading-relaxed text-ivory/75">
                  Home to the Congo Rainforest and rare species found nowhere else on Earth — and a rich legacy of music, art, and spiritual tradition.
                </p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {["Congo Rainforest", "Mountain gorillas", "Okapi", "Bonobos", "Music", "Art", "Spiritual tradition"].map((c) => (
                    <li key={c} className="rounded-full border border-ivory/25 px-3.5 py-1.5 text-xs uppercase tracking-wider text-ivory/85">{c}</li>
                  ))}
                </ul>
              </div>
            </article>
          </Reveal>
        </div>

        {/* 06 — INDUSTRIAL & ENERGY */}
        <div className="mt-24" data-testid="why-congo-industrial">
          <Reveal>
            <BlockNumber n="06" />
            <h3 className="mt-4 max-w-3xl font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">Industrial &amp; energy power.</h3>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRIAL.map((item, i) => (
              <Reveal key={item.name} delay={(i % 3) * 0.06} y={14}>
                <div className="h-full rounded-xl border border-ivory/10 bg-ivory/5 p-6" data-testid={`why-congo-industrial-${i + 1}`}>
                  <p className="font-display text-xl font-semibold italic text-gold">{item.name}</p>
                  <p className="mt-2 text-sm leading-relaxed text-ivory/70">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* 07 — HISTORY */}
        <div className="mt-24 border-y border-ivory/10 bg-charcoal/60 py-16 sm:py-20" data-testid="why-congo-history">
          <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
            <Reveal>
              <BlockNumber n="07" />
              <h3 className="mt-4 font-display text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">The History We Must Not Forget</h3>
              <p className="mx-auto mt-7 max-w-3xl text-left text-lg leading-relaxed text-ivory/80 sm:text-center">
                Congo's modern history was shaped by one of the darkest chapters in colonialism. King Leopold II claimed Congo as his personal possession. Millions were forced to harvest rubber and ivory under brutal conditions — hands cut off, families torn apart, villages burned.
              </p>
              <blockquote className="mx-auto mt-10 max-w-3xl font-display text-2xl font-medium italic leading-snug text-gold sm:text-3xl lg:text-4xl" data-testid="why-congo-history-quote">
                "Congo became the chained elephant of the world: powerful, rich, and majestic, yet violently restrained for the profit of others."
              </blockquote>
              <p className="mx-auto mt-10 max-w-2xl text-left text-lg leading-relaxed text-ivory/80 sm:text-center">
                This legacy of extraction never fully ended. It simply changed forms.
              </p>
            </Reveal>
          </div>
        </div>

        {/* WHY WE EXIST */}
        <div className="mt-28 text-center" data-testid="why-congo-why-we-exist">
          <Reveal>
            <p className="eyebrow text-gold/90">Why We Exist</p>
            <div className="mx-auto mt-10 max-w-2xl space-y-7" data-testid="why-congo-why-we-exist-statement">
              <p className="text-lg leading-relaxed text-ivory/85 sm:text-xl sm:leading-[1.85]">
                Congo is the engine that powers our modern world, yet its people have carried the weight of global progress without sharing in its rewards. While its minerals fuel our phones, our cars, and our future, only a fraction of this vast land is mined — proving that the issue is <em className="italic text-gold/95">not scarcity, but inequity.</em>
              </p>
              <p className="font-display text-xl italic leading-relaxed text-gold/95 sm:text-2xl">
                The land is abundant. The people are overlooked.
              </p>
              <p className="text-lg leading-relaxed text-ivory/85 sm:text-xl sm:leading-[1.85]">
                And so we choose to stand with them. We honour Congo's gifts by fighting for dignity, safety, and a future where the communities behind the world's greatest resources finally thrive.
              </p>
              <p className="text-lg leading-relaxed text-ivory/85 sm:text-xl sm:leading-[1.85]">
                We are grateful for Congo — not just for what it gives, but for <em className="italic text-gold/95">the resilience, brilliance, and humanity of its people.</em>
              </p>
            </div>
            <div className="mt-14">
              <DonateButton testid="why-congo-donate-button" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
